/* Copyright (c) 2020-present CloudIO Inc */

export const code = `import { parseISO } from 'date-fns';
import * as d3 from 'd3';
import { D3Props, d3uid } from 'cloudio';

const TOOLTIP_ON_TOP = true;

// mount function is called when the D3 Chart item is mounted
// do your initailization here. This function is called only once
// when the D3 Chart item is mounted. if the user navigates away
// from the page and comes back, this function will be called again
function mount(props: D3Props) {
  console.log('mount', props);
}

// render function is called whenever the props passed to the D3 Chart item changes
// e.g. when the dataset or the width or height of the D3 Chart item changes
function render(props: D3Props) {
  console.log('render', props);
  const {
    dataset, // this is the dataset passed to the D3 Chart item from the parent data store reference defined above the D3 Chart item
    svg, // this is the root svg element of the D3 Chart item
    width, // this is the width of the D3 Chart item
    height, // this is the height of the D3 Chart item
    onClickTrigger, // this is the callback function to invoke any On Click Triggers defined inside the D3 Chart item
    tooltipRef, // the tooltip div element
    rootRef, // the root div element
    theme, // the material-ui theme object
  } = props;
  // Accessors
  let xAccessor = (d: Record<string, any>) => parseISO(d.date);
  let yAccessor = (d: Record<string, any>) => d.temperature;

  // Dimensions
  let dimensions = {
    width,
    height: height,
    margins: 50,
    containerWidth: 0,
    containerHeight: 0,
  };

  dimensions.containerWidth = dimensions.width - dimensions.margins * 2;
  dimensions.containerHeight = dimensions.height - dimensions.margins * 2;

  const container = svg
    .append('g')
    .classed('container', true)
    .attr(
      'transform',
      \`translate(\${dimensions.margins}, \${dimensions.margins})\`,
    );

  d3.select(tooltipRef.current).selectChild('.d3-tooltip').remove();

  const tooltip = d3
    .select(tooltipRef.current)
    .append('div')
    .style('display', 'none')
    .style('position', 'fixed')
    .attr('class', 'd3-tooltip')
    .style('background-color', '#444') // theme.palette.background.paper
    .style('border', 'solid')
    .style('z-index', '9999')
    .style('border-color', theme.palette.divider)
    .style('color', '#fff') // theme.palette.text.primary
    .style('border-width', '1px')
    .style('border-radius', '8px')
    .style('padding', '8px')
    .style('white-space', 'nowrap');
  let record: Record<string, unknown> | undefined = undefined;
  const tooltipDot = container
    .append('circle')
    .classed('tool-tip-dot', true)
    .attr('r', 8)
    .attr('fill', 'transparent')
    .attr('stroke', theme.palette.divider)
    .attr('stroke-width', 2)
    .style('opacity', 0)
    .style('cursor', 'pointer')
    .on('mouseenter', function () {
      tooltipDot.attr('stroke', theme.palette.primary.main);
    })
    .on('mouseleave', function () {
      tooltipDot.attr('stroke', theme.palette.divider);
    })
    .on('click', function (event) {
      if (record) {
        onClickTrigger?.(event, { record }); // this will invoke any On Click Triggers defined inside the D3 Chart item
      }
    });

  const vertical = container
    .append('line') // this is the black vertical line to follow mouse
    .attr('class', 'mouseLine')
    .attr('y1', dimensions.containerHeight)
    .attr('y2', 0)
    .style('stroke', theme.palette.divider)
    .style('stroke-width', '1px')
    .style('opacity', '0');

  const horizontal = container
    .append('line') // this is the black vertical line to follow mouse
    .attr('class', 'mouseLine')
    .attr('x1', 0)
    .attr('x2', dimensions.containerWidth)
    .style('stroke', theme.palette.divider)
    .style('stroke-width', '1px')
    .style('opacity', '0');

  const yDomain = d3.extent(dataset, yAccessor) as number[];
  const xDomain = d3.extent(dataset, xAccessor) as Date[];
  // Scales
  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .nice()
    .range([dimensions.containerHeight, 0]);

  const xScale = d3
    .scaleUtc()
    .domain(xDomain)
    .range([0, dimensions.containerWidth]);

  const gradient = d3uid();

  const color = d3.scaleSequential(yScale.domain(), d3.interpolateTurbo);
  svg
    .append('linearGradient')
    .attr('id', gradient.id)
    .attr('gradientUnits', 'userSpaceOnUse')
    .attr('x1', 0)
    .attr('y1', dimensions.containerHeight)
    .attr('x2', 0)
    .attr('y2', 0)
    .selectAll('stop')
    .data(d3.ticks(0, 1, 10))
    .join('stop')
    .attr('offset', (d) => d)
    .attr('stop-color', color.interpolator());

  // Line Generator
  const lineGenerator = d3
    .line()
    .x((d) => xScale(xAccessor(d)))
    .y((d) => yScale(yAccessor(d)));

  // Draw Line
  container
    .append('path')
    .datum(dataset)
    // @ts-ignore
    .attr('d', lineGenerator)
    .attr('fill', 'none')
    // @ts-ignore
    .attr('stroke', gradient)
    .attr('stroke-width', 2);

  // Axis
  const yAxis = d3.axisLeft(yScale).tickFormat((d) => \`\${d} F\`);

  const y = container
    .append('g')
    .style('color', theme.palette.divider)
    .classed('yAxis', true)
    .call(yAxis);
  y.selectAll('text').style('color', theme.palette.text.secondary);

  const xAxis = d3.axisBottom(xScale);

  const x = container
    .append('g')
    .style('color', theme.palette.divider)
    .classed('xAxis', true)
    .style('transform', \`translateY(\${dimensions.containerHeight}px)\`)
    .call(xAxis);
  x.selectAll('text')
    .attr('transform', 'rotate(30)')
    .style('text-anchor', 'start')
    .style('color', theme.palette.text.secondary);

  // Tooltip
  const mouseTracker = container
    .append('rect')
    .classed('mouse-tracker', true)
    .attr('width', dimensions.containerWidth)
    .attr('height', dimensions.containerHeight)
    .style('opacity', 0)
    .on('touchmouse mousemove', function (event) {
      const mousePos = d3.pointer(event, this);
      // x coordinate stored in mousePos index 0
      const date = xScale.invert(mousePos[0]);
      // Custom Bisector - left, center, right
      const dateBisector = d3.bisector(xAccessor).center;
      const bisectionIndex = dateBisector(dataset, date);
      // math.max prevents negative index reference error
      record = dataset[Math.max(0, bisectionIndex)];
      const left = xScale(xAccessor(record));
      const top = yScale(yAccessor(record));
      // Update Image
      tooltipDot.style('opacity', 1).attr('cx', left).attr('cy', top).raise();

      const { top: mtop, left: mleft } = mouseTracker
        .node()!
        .getBoundingClientRect();
      const dateFormatter = d3.timeFormat('%B %-d, %Y');

      tooltip
        .style('display', 'block')
        .html(
          \`\${yAccessor(record)}&#8457; on \${dateFormatter(
  xAccessor(record),
)}\`,
        )
        .style('top', \`\${(TOOLTIP_ON_TOP ? 0 : top) + mtop}px\`)
        .style(
          'transform',
          \`translate(-50%, \${
            TOOLTIP_ON_TOP ? 'calc(-100% - 8px)' : 'calc(-100% - 20px)'
          })\`,
        )
        .style('left', \`\${left + mleft}px\`);

      const {
        left: tl,
        top: tt,
        width: tw,
        height: th,
      } = tooltip.node()!.getBoundingClientRect()!;

      const ww = window.innerWidth;
      const wh = window.innerHeight;

      if (tl + tw > ww - 16) {
        tooltip
          .style('left', \`\${ww - tw - 16}px\`)
          .style(
            'transform',
            \`translate(0%, \${
              TOOLTIP_ON_TOP ? 'calc(-100% - 8px)' : 'calc(-100% - 20px)'
            })\`,
          );
      }
      if (tl < 16) {
        tooltip
          .style('left', '16px')
          .style(
            'transform',
            \`translate(0%, \${
              TOOLTIP_ON_TOP ? 'calc(-100% - 8px)' : 'calc(-100% - 20px)'
            })\`,
          );
      }

      vertical.attr('x1', left).attr('x2', left).style('opacity', 1);
      horizontal
        .attr('y1', yScale(yAccessor(record)))
        .attr('y2', yScale(yAccessor(record)))
        .style('opacity', 1);
    });
  d3.select(rootRef.current).on('mouseleave', function () {
    vertical.style('opacity', 0);
    horizontal.style('opacity', 0);
    tooltipDot.style('opacity', 0);
    tooltip.style('display', 'none');
  });
}

// mount function is called when the D3 Chart item is unmounted
// use this to clean up any work done in the mount function
// this function will be called when the D3 Chart item is unmounted
// due to the user navigating away from the page or the D3 Chart item being removed from the dashboard
function unmount(props: D3Props) {
  console.log('unmount', props);
}

export default { render, mount, unmount };`;

export const data = [
  { date: '2011-10-01', temperature: 62.7 },
  { date: '2011-10-02', temperature: 59.9 },
  { date: '2011-10-03', temperature: 59.1 },
  { date: '2011-10-04', temperature: 58.8 },
  { date: '2011-10-05', temperature: 58.7 },
  { date: '2011-10-06', temperature: 57.0 },
  { date: '2011-10-07', temperature: 56.7 },
  { date: '2011-10-08', temperature: 56.8 },
  { date: '2011-10-09', temperature: 56.7 },
  { date: '2011-10-10', temperature: 60.1 },
  { date: '2011-10-11', temperature: 61.1 },
  { date: '2011-10-12', temperature: 61.5 },
  { date: '2011-10-13', temperature: 64.3 },
  { date: '2011-10-14', temperature: 67.1 },
  { date: '2011-10-15', temperature: 64.6 },
  { date: '2011-10-16', temperature: 61.6 },
  { date: '2011-10-17', temperature: 61.1 },
  { date: '2011-10-18', temperature: 59.2 },
  { date: '2011-10-19', temperature: 58.9 },
  { date: '2011-10-20', temperature: 57.2 },
  { date: '2011-10-21', temperature: 56.4 },
  { date: '2011-10-22', temperature: 60.7 },
  { date: '2011-10-23', temperature: 65.1 },
  { date: '2011-10-24', temperature: 60.9 },
  { date: '2011-10-25', temperature: 56.1 },
  { date: '2011-10-26', temperature: 54.6 },
  { date: '2011-10-27', temperature: 56.1 },
  { date: '2011-10-28', temperature: 58.1 },
  { date: '2011-10-29', temperature: 57.5 },
  { date: '2011-10-30', temperature: 57.7 },
  { date: '2011-10-31', temperature: 55.1 },
  { date: '2011-11-01', temperature: 57.9 },
  { date: '2011-11-02', temperature: 64.6 },
  { date: '2011-11-03', temperature: 56.2 },
  { date: '2011-11-04', temperature: 50.5 },
  { date: '2011-11-05', temperature: 51.3 },
  { date: '2011-11-06', temperature: 52.6 },
  { date: '2011-11-07', temperature: 51.4 },
  { date: '2011-11-08', temperature: 50.6 },
  { date: '2011-11-09', temperature: 54.6 },
  { date: '2011-11-10', temperature: 55.6 },
  { date: '2011-11-11', temperature: 53.9 },
  { date: '2011-11-12', temperature: 54.0 },
  { date: '2011-11-13', temperature: 53.8 },
  { date: '2011-11-14', temperature: 53.5 },
  { date: '2011-11-15', temperature: 53.4 },
  { date: '2011-11-16', temperature: 52.2 },
  { date: '2011-11-17', temperature: 52.7 },
  { date: '2011-11-18', temperature: 53.1 },
  { date: '2011-11-19', temperature: 49.0 },
  { date: '2011-11-20', temperature: 50.4 },
  { date: '2011-11-21', temperature: 51.1 },
  { date: '2011-11-22', temperature: 52.3 },
  { date: '2011-11-23', temperature: 54.6 },
  { date: '2011-11-24', temperature: 55.1 },
  { date: '2011-11-25', temperature: 51.5 },
  { date: '2011-11-26', temperature: 53.6 },
  { date: '2011-11-27', temperature: 52.3 },
  { date: '2011-11-28', temperature: 51.0 },
  { date: '2011-11-29', temperature: 49.5 },
  { date: '2011-11-30', temperature: 49.8 },
  { date: '2011-12-01', temperature: 60.4 },
  { date: '2011-12-02', temperature: 62.2 },
  { date: '2011-12-03', temperature: 58.3 },
  { date: '2011-12-04', temperature: 52.7 },
  { date: '2011-12-05', temperature: 51.5 },
  { date: '2011-12-06', temperature: 49.9 },
  { date: '2011-12-07', temperature: 48.6 },
  { date: '2011-12-08', temperature: 46.4 },
  { date: '2011-12-09', temperature: 49.8 },
  { date: '2011-12-10', temperature: 52.1 },
  { date: '2011-12-11', temperature: 48.8 },
  { date: '2011-12-12', temperature: 47.4 },
  { date: '2011-12-13', temperature: 47.2 },
  { date: '2011-12-14', temperature: 46.1 },
  { date: '2011-12-15', temperature: 48.8 },
  { date: '2011-12-16', temperature: 47.9 },
  { date: '2011-12-17', temperature: 49.8 },
  { date: '2011-12-18', temperature: 49.1 },
  { date: '2011-12-19', temperature: 48.3 },
  { date: '2011-12-20', temperature: 49.3 },
  { date: '2011-12-21', temperature: 48.4 },
  { date: '2011-12-22', temperature: 53.3 },
  { date: '2011-12-23', temperature: 47.5 },
  { date: '2011-12-24', temperature: 47.9 },
  { date: '2011-12-25', temperature: 48.9 },
  { date: '2011-12-26', temperature: 45.9 },
  { date: '2011-12-27', temperature: 47.2 },
  { date: '2011-12-28', temperature: 48.9 },
  { date: '2011-12-29', temperature: 50.9 },
  { date: '2011-12-30', temperature: 52.9 },
  { date: '2011-12-31', temperature: 50.1 },
  { date: '2012-01-01', temperature: 53.9 },
  { date: '2012-01-02', temperature: 53.1 },
  { date: '2012-01-03', temperature: 49.7 },
  { date: '2012-01-04', temperature: 52.7 },
  { date: '2012-01-05', temperature: 52.6 },
  { date: '2012-01-06', temperature: 49.0 },
  { date: '2012-01-07', temperature: 51.0 },
  { date: '2012-01-08', temperature: 56.8 },
  { date: '2012-01-09', temperature: 52.3 },
  { date: '2012-01-10', temperature: 51.6 },
  { date: '2012-01-11', temperature: 49.8 },
  { date: '2012-01-12', temperature: 51.9 },
  { date: '2012-01-13', temperature: 53.7 },
  { date: '2012-01-14', temperature: 52.9 },
  { date: '2012-01-15', temperature: 49.7 },
  { date: '2012-01-16', temperature: 45.3 },
  { date: '2012-01-17', temperature: 43.6 },
  { date: '2012-01-18', temperature: 45.0 },
  { date: '2012-01-19', temperature: 47.3 },
  { date: '2012-01-20', temperature: 51.4 },
  { date: '2012-01-21', temperature: 53.7 },
  { date: '2012-01-22', temperature: 48.3 },
  { date: '2012-01-23', temperature: 52.9 },
  { date: '2012-01-24', temperature: 49.1 },
  { date: '2012-01-25', temperature: 52.1 },
  { date: '2012-01-26', temperature: 53.6 },
  { date: '2012-01-27', temperature: 50.4 },
  { date: '2012-01-28', temperature: 50.3 },
  { date: '2012-01-29', temperature: 53.8 },
  { date: '2012-01-30', temperature: 51.9 },
  { date: '2012-01-31', temperature: 50.0 },
  { date: '2012-02-01', temperature: 50.0 },
  { date: '2012-02-02', temperature: 51.3 },
  { date: '2012-02-03', temperature: 51.5 },
  { date: '2012-02-04', temperature: 52.0 },
  { date: '2012-02-05', temperature: 53.8 },
  { date: '2012-02-06', temperature: 54.6 },
  { date: '2012-02-07', temperature: 54.3 },
  { date: '2012-02-08', temperature: 51.9 },
  { date: '2012-02-09', temperature: 53.8 },
  { date: '2012-02-10', temperature: 53.9 },
  { date: '2012-02-11', temperature: 52.3 },
  { date: '2012-02-12', temperature: 50.1 },
  { date: '2012-02-13', temperature: 49.5 },
  { date: '2012-02-14', temperature: 48.6 },
  { date: '2012-02-15', temperature: 49.9 },
  { date: '2012-02-16', temperature: 52.4 },
  { date: '2012-02-17', temperature: 49.9 },
  { date: '2012-02-18', temperature: 51.6 },
  { date: '2012-02-19', temperature: 47.8 },
  { date: '2012-02-20', temperature: 48.7 },
  { date: '2012-02-21', temperature: 49.7 },
  { date: '2012-02-22', temperature: 53.4 },
  { date: '2012-02-23', temperature: 54.1 },
  { date: '2012-02-24', temperature: 55.9 },
  { date: '2012-02-25', temperature: 51.7 },
  { date: '2012-02-26', temperature: 47.7 },
  { date: '2012-02-27', temperature: 45.4 },
  { date: '2012-02-28', temperature: 47.0 },
  { date: '2012-02-29', temperature: 49.8 },
  { date: '2012-03-01', temperature: 48.9 },
  { date: '2012-03-02', temperature: 48.1 },
  { date: '2012-03-03', temperature: 50.7 },
  { date: '2012-03-04', temperature: 55.0 },
  { date: '2012-03-05', temperature: 48.8 },
  { date: '2012-03-06', temperature: 48.4 },
  { date: '2012-03-07', temperature: 49.9 },
  { date: '2012-03-08', temperature: 49.2 },
  { date: '2012-03-09', temperature: 51.7 },
  { date: '2012-03-10', temperature: 49.3 },
  { date: '2012-03-11', temperature: 50.0 },
  { date: '2012-03-12', temperature: 48.6 },
  { date: '2012-03-13', temperature: 53.9 },
  { date: '2012-03-14', temperature: 55.2 },
  { date: '2012-03-15', temperature: 55.9 },
  { date: '2012-03-16', temperature: 54.6 },
  { date: '2012-03-17', temperature: 48.2 },
  { date: '2012-03-18', temperature: 47.1 },
  { date: '2012-03-19', temperature: 45.8 },
  { date: '2012-03-20', temperature: 49.7 },
  { date: '2012-03-21', temperature: 51.4 },
  { date: '2012-03-22', temperature: 51.4 },
  { date: '2012-03-23', temperature: 48.4 },
  { date: '2012-03-24', temperature: 49.0 },
  { date: '2012-03-25', temperature: 46.4 },
  { date: '2012-03-26', temperature: 49.7 },
  { date: '2012-03-27', temperature: 54.1 },
  { date: '2012-03-28', temperature: 54.6 },
  { date: '2012-03-29', temperature: 52.3 },
  { date: '2012-03-30', temperature: 54.5 },
  { date: '2012-03-31', temperature: 56.2 },
  { date: '2012-04-01', temperature: 51.1 },
  { date: '2012-04-02', temperature: 50.5 },
  { date: '2012-04-03', temperature: 52.2 },
  { date: '2012-04-04', temperature: 50.6 },
  { date: '2012-04-05', temperature: 47.9 },
  { date: '2012-04-06', temperature: 47.4 },
  { date: '2012-04-07', temperature: 49.4 },
  { date: '2012-04-08', temperature: 50.0 },
  { date: '2012-04-09', temperature: 51.3 },
  { date: '2012-04-10', temperature: 53.8 },
  { date: '2012-04-11', temperature: 52.9 },
  { date: '2012-04-12', temperature: 53.9 },
  { date: '2012-04-13', temperature: 50.2 },
  { date: '2012-04-14', temperature: 50.9 },
  { date: '2012-04-15', temperature: 51.5 },
  { date: '2012-04-16', temperature: 51.9 },
  { date: '2012-04-17', temperature: 53.2 },
  { date: '2012-04-18', temperature: 53.0 },
  { date: '2012-04-19', temperature: 55.1 },
  { date: '2012-04-20', temperature: 55.8 },
  { date: '2012-04-21', temperature: 58.0 },
  { date: '2012-04-22', temperature: 52.8 },
  { date: '2012-04-23', temperature: 55.1 },
  { date: '2012-04-24', temperature: 57.9 },
  { date: '2012-04-25', temperature: 57.5 },
  { date: '2012-04-26', temperature: 55.3 },
  { date: '2012-04-27', temperature: 53.5 },
  { date: '2012-04-28', temperature: 54.7 },
  { date: '2012-04-29', temperature: 54.0 },
  { date: '2012-04-30', temperature: 53.4 },
  { date: '2012-05-01', temperature: 52.7 },
  { date: '2012-05-02', temperature: 50.7 },
  { date: '2012-05-03', temperature: 52.6 },
  { date: '2012-05-04', temperature: 53.4 },
  { date: '2012-05-05', temperature: 53.1 },
  { date: '2012-05-06', temperature: 56.5 },
  { date: '2012-05-07', temperature: 55.3 },
  { date: '2012-05-08', temperature: 52.0 },
  { date: '2012-05-09', temperature: 52.4 },
  { date: '2012-05-10', temperature: 53.4 },
  { date: '2012-05-11', temperature: 53.1 },
  { date: '2012-05-12', temperature: 49.9 },
  { date: '2012-05-13', temperature: 52.0 },
  { date: '2012-05-14', temperature: 56.0 },
  { date: '2012-05-15', temperature: 53.0 },
  { date: '2012-05-16', temperature: 51.0 },
  { date: '2012-05-17', temperature: 51.4 },
  { date: '2012-05-18', temperature: 52.2 },
  { date: '2012-05-19', temperature: 52.4 },
  { date: '2012-05-20', temperature: 54.5 },
  { date: '2012-05-21', temperature: 52.8 },
  { date: '2012-05-22', temperature: 53.9 },
  { date: '2012-05-23', temperature: 56.5 },
  { date: '2012-05-24', temperature: 54.7 },
  { date: '2012-05-25', temperature: 52.5 },
  { date: '2012-05-26', temperature: 52.1 },
  { date: '2012-05-27', temperature: 52.2 },
  { date: '2012-05-28', temperature: 52.9 },
  { date: '2012-05-29', temperature: 52.1 },
  { date: '2012-05-30', temperature: 52.1 },
  { date: '2012-05-31', temperature: 53.3 },
  { date: '2012-06-01', temperature: 54.8 },
  { date: '2012-06-02', temperature: 54.0 },
  { date: '2012-06-03', temperature: 52.3 },
  { date: '2012-06-04', temperature: 55.3 },
  { date: '2012-06-05', temperature: 53.5 },
  { date: '2012-06-06', temperature: 54.1 },
  { date: '2012-06-07', temperature: 53.9 },
  { date: '2012-06-08', temperature: 54.4 },
  { date: '2012-06-09', temperature: 55.0 },
  { date: '2012-06-10', temperature: 60.0 },
  { date: '2012-06-11', temperature: 57.2 },
  { date: '2012-06-12', temperature: 55.1 },
  { date: '2012-06-13', temperature: 53.3 },
  { date: '2012-06-14', temperature: 53.4 },
  { date: '2012-06-15', temperature: 54.6 },
  { date: '2012-06-16', temperature: 57.0 },
  { date: '2012-06-17', temperature: 55.6 },
  { date: '2012-06-18', temperature: 52.5 },
  { date: '2012-06-19', temperature: 53.9 },
  { date: '2012-06-20', temperature: 55.3 },
  { date: '2012-06-21', temperature: 53.3 },
  { date: '2012-06-22', temperature: 54.1 },
  { date: '2012-06-23', temperature: 55.2 },
  { date: '2012-06-24', temperature: 55.8 },
  { date: '2012-06-25', temperature: 56.8 },
  { date: '2012-06-26', temperature: 57.5 },
  { date: '2012-06-27', temperature: 57.7 },
  { date: '2012-06-28', temperature: 56.6 },
  { date: '2012-06-29', temperature: 56.4 },
  { date: '2012-06-30', temperature: 58.4 },
  { date: '2012-07-01', temperature: 58.8 },
  { date: '2012-07-02', temperature: 56.4 },
  { date: '2012-07-03', temperature: 56.5 },
  { date: '2012-07-04', temperature: 55.8 },
  { date: '2012-07-05', temperature: 54.8 },
  { date: '2012-07-06', temperature: 54.9 },
  { date: '2012-07-07', temperature: 54.7 },
  { date: '2012-07-08', temperature: 52.8 },
  { date: '2012-07-09', temperature: 53.7 },
  { date: '2012-07-10', temperature: 53.1 },
  { date: '2012-07-11', temperature: 52.7 },
  { date: '2012-07-12', temperature: 52.0 },
  { date: '2012-07-13', temperature: 53.4 },
  { date: '2012-07-14', temperature: 54.0 },
  { date: '2012-07-15', temperature: 54.0 },
  { date: '2012-07-16', temperature: 54.5 },
  { date: '2012-07-17', temperature: 56.7 },
  { date: '2012-07-18', temperature: 57.5 },
  { date: '2012-07-19', temperature: 57.1 },
  { date: '2012-07-20', temperature: 58.1 },
  { date: '2012-07-21', temperature: 57.6 },
  { date: '2012-07-22', temperature: 56.0 },
  { date: '2012-07-23', temperature: 56.6 },
  { date: '2012-07-24', temperature: 57.8 },
  { date: '2012-07-25', temperature: 57.5 },
  { date: '2012-07-26', temperature: 56.4 },
  { date: '2012-07-27', temperature: 55.3 },
  { date: '2012-07-28', temperature: 55.0 },
  { date: '2012-07-29', temperature: 55.6 },
  { date: '2012-07-30', temperature: 55.6 },
  { date: '2012-07-31', temperature: 55.9 },
  { date: '2012-08-01', temperature: 55.4 },
  { date: '2012-08-02', temperature: 54.4 },
  { date: '2012-08-03', temperature: 53.7 },
  { date: '2012-08-04', temperature: 54.1 },
  { date: '2012-08-05', temperature: 57.8 },
  { date: '2012-08-06', temperature: 58.2 },
  { date: '2012-08-07', temperature: 58.0 },
  { date: '2012-08-08', temperature: 57.0 },
  { date: '2012-08-09', temperature: 55.0 },
  { date: '2012-08-10', temperature: 54.8 },
  { date: '2012-08-11', temperature: 53.0 },
  { date: '2012-08-12', temperature: 52.5 },
  { date: '2012-08-13', temperature: 53.3 },
  { date: '2012-08-14', temperature: 53.9 },
  { date: '2012-08-15', temperature: 56.2 },
  { date: '2012-08-16', temperature: 57.1 },
  { date: '2012-08-17', temperature: 55.3 },
  { date: '2012-08-18', temperature: 56.2 },
  { date: '2012-08-19', temperature: 54.3 },
  { date: '2012-08-20', temperature: 53.1 },
  { date: '2012-08-21', temperature: 53.4 },
  { date: '2012-08-22', temperature: 54.5 },
  { date: '2012-08-23', temperature: 55.7 },
  { date: '2012-08-24', temperature: 54.8 },
  { date: '2012-08-25', temperature: 53.8 },
  { date: '2012-08-26', temperature: 56.5 },
  { date: '2012-08-27', temperature: 58.3 },
  { date: '2012-08-28', temperature: 58.7 },
  { date: '2012-08-29', temperature: 57.5 },
  { date: '2012-08-30', temperature: 55.9 },
  { date: '2012-08-31', temperature: 55.4 },
  { date: '2012-09-01', temperature: 55.7 },
  { date: '2012-09-02', temperature: 53.1 },
  { date: '2012-09-03', temperature: 53.5 },
  { date: '2012-09-04', temperature: 52.5 },
  { date: '2012-09-05', temperature: 54.5 },
  { date: '2012-09-06', temperature: 56.3 },
  { date: '2012-09-07', temperature: 56.4 },
  { date: '2012-09-08', temperature: 56.5 },
  { date: '2012-09-09', temperature: 56.4 },
  { date: '2012-09-10', temperature: 55.4 },
  { date: '2012-09-11', temperature: 56.2 },
  { date: '2012-09-12', temperature: 55.7 },
  { date: '2012-09-13', temperature: 54.3 },
  { date: '2012-09-14', temperature: 55.2 },
  { date: '2012-09-15', temperature: 54.3 },
  { date: '2012-09-16', temperature: 52.9 },
  { date: '2012-09-17', temperature: 54.8 },
  { date: '2012-09-18', temperature: 54.8 },
  { date: '2012-09-19', temperature: 56.8 },
  { date: '2012-09-20', temperature: 55.4 },
  { date: '2012-09-21', temperature: 55.8 },
  { date: '2012-09-22', temperature: 55.9 },
  { date: '2012-09-23', temperature: 52.8 },
  { date: '2012-09-24', temperature: 54.5 },
  { date: '2012-09-25', temperature: 53.3 },
  { date: '2012-09-26', temperature: 53.6 },
  { date: '2012-09-27', temperature: 52.1 },
  { date: '2012-09-28', temperature: 52.6 },
  { date: '2012-09-29', temperature: 53.9 },
  { date: '2012-09-30', temperature: 55.1 },
];
